/**
 * Utility functions for the Action Detail Modal feature.
 * Includes:
 * - Action filtering logic (AND across categories, OR within)
 * - Cross-filter count computation
 * - Possession-level deduplication (chanceId)
 * - Display formatting helpers
 *
 * Reuses drawCourt and createCoordinateTransformer from ShotDetailModal/utils.
 */

import {
  Action,
  ActionFilters,
  ActionType,
  ActionSize,
  ChanceLineup,
  LineupKey,
  LINEUP_KEYS,
  PrimaryOutcome,
  TYPE_SPECIFIC_FIELDS,
  ALL_ACTION_TYPES,
  isLineupKey,
  isShotFilterKey,
} from "./types";
import { Shot } from "../ShotDetailModal/types";
import {
  formatPct as _formatPct,
  getHeatColor as _getHeatColor,
  computeFgPct,
  isMidShot,
  isRimShot,
  partitionShotsByGroup as _partitionShotsByGroup,
  formatShotGroupValue as _formatShotGroupValue,
  SHOT_GROUP_LABELS as _SHOT_GROUP_LABELS,
} from "../ShotDetailModal/utils";
import { pickTypeLabel } from "../pickTypes";

// Re-export shared court utilities so consumers don't need to import from two places
export {
  drawCourt,
  createCoordinateTransformer,
} from "../ShotDetailModal/utils";
export {
  getVisibleCourtLength,
  COURT_CONSTANTS,
} from "../ShotDetailModal/types";

// Re-export shot utilities for existing consumers (ActionDetailFilters, etc.)
export const formatPct = _formatPct;
export const getHeatColor = _getHeatColor;
export const partitionShotsByGroup = _partitionShotsByGroup;
export const formatShotGroupValue = _formatShotGroupValue;
export const SHOT_GROUP_LABELS = _SHOT_GROUP_LABELS;

// ============================================================================
// Action Filtering
// ============================================================================

type LineupsMap = Record<string, ChanceLineup>;

/**
 * Side of the ball a lineup key operates on.
 */
function lineupSide(key: LineupKey): "off" | "def" {
  return key === "offenseOnCourt" || key === "offenseOffCourt" ? "off" : "def";
}

/**
 * Whether a lineup key filters for presence on court (vs absence).
 */
function lineupOnCourt(key: LineupKey): boolean {
  return key === "offenseOnCourt" || key === "defenseOnCourt";
}

/**
 * Apply the active lineup filters (offense/defense on/off-court) to a single
 * action. Returns false when any active lineup filter fails — which includes
 * the case where the action's chance has no lineup data, since we can't verify
 * presence/absence without it.
 */
function passesLineupFilters(
  action: Action,
  filters: ActionFilters,
  lineups: LineupsMap,
): boolean {
  for (const key of LINEUP_KEYS) {
    const sel = filters[key];
    if (sel.size === 0) continue;

    const cid = action.chanceId;
    if (!cid) return false;
    const lu = lineups[cid];
    if (!lu) return false;

    const pool = new Set(lu[lineupSide(key)]);
    const mode = filters.lineupMode[key];
    const onCourt = lineupOnCourt(key);

    // on-court: selected players must (all/any) be in the lineup
    // off-court: selected players must (all/any) be NOT in the lineup
    let pass: boolean;
    if (onCourt) {
      pass = mode === "all"
        ? allIn(sel, pool)
        : anyIn(sel, pool);
    } else {
      pass = mode === "all"
        ? allOut(sel, pool)
        : anyOut(sel, pool);
    }
    if (!pass) return false;
  }
  return true;
}

function allIn(sel: Set<number>, pool: Set<number>): boolean {
  for (const p of sel) if (!pool.has(p)) return false;
  return true;
}
function anyIn(sel: Set<number>, pool: Set<number>): boolean {
  for (const p of sel) if (pool.has(p)) return true;
  return false;
}
function allOut(sel: Set<number>, pool: Set<number>): boolean {
  for (const p of sel) if (pool.has(p)) return false;
  return true;
}
function anyOut(sel: Set<number>, pool: Set<number>): boolean {
  for (const p of sel) if (!pool.has(p)) return true;
  return false;
}

/**
 * Check whether a real (non-no_action) action passes all action-level filters.
 * Called by applyActionFilters; no_action actions bypass this entirely.
 */
function passesActionFilters(action: Action, filters: ActionFilters): boolean {
  if (!filters.actionType.has(action.actionType)) return false;
  if (
    filters.actionSimple.size > 0 &&
    !filters.actionSimple.has(action.actionSimple)
  )
    return false;
  if (!filters.actionSize.has(action.actionSize)) return false;

  const directValue = action.direct === true ? "direct" : "indirect";
  if (!filters.direct.has(directValue)) return false;

  if (
    action.primaryOutcome !== null &&
    !filters.primaryOutcome.has(action.primaryOutcome)
  )
    return false;

  if (
    filters.primaryPlayer.size > 0 &&
    (action.primaryPlayer === null ||
      !filters.primaryPlayer.has(action.primaryPlayer))
  )
    return false;
  if (
    filters.secondaryPlayer.size > 0 &&
    (action.secondaryPlayer === null ||
      !filters.secondaryPlayer.has(action.secondaryPlayer))
  )
    return false;
  if (
    filters.primaryDefender.size > 0 &&
    (action.primaryDefender === null ||
      !filters.primaryDefender.has(action.primaryDefender))
  )
    return false;
  if (
    filters.secondaryDefender.size > 0 &&
    (action.secondaryDefender === null ||
      !filters.secondaryDefender.has(action.secondaryDefender))
  )
    return false;

  for (const [field, selectedValues] of Object.entries(filters.typeSpecific)) {
    if (selectedValues.size === 0) continue;
    const rawValue = (action as any)[field];
    if (rawValue === undefined) continue;
    if (!selectedValues.has(String(rawValue))) return false;
  }

  return true;
}

/**
 * Apply filter state to an array of actions.
 * - no_action entries bypass all action-level filters — controlled only by actionPresence
 * - Real actions must pass actionPresence AND all action-level filters
 * - Multiple selections within a category = OR logic
 * - Selections across categories = AND logic
 */
/**
 * Returns a copy of `filters` with the specified lineup selections cleared.
 * Used when computing the pool for lineup breakdowns — only in-breakdown
 * lineup axes should be stripped so out-of-breakdown lineup filters still
 * apply. Defaults to all four lineup keys when no `keys` arg is provided.
 */
export function stripLineupFilters(
  filters: ActionFilters,
  keys: Iterable<LineupKey> = LINEUP_KEYS,
): ActionFilters {
  const out: ActionFilters = { ...filters };
  for (const k of Array.from(keys)) {
    if (k === "offenseOnCourt") out.offenseOnCourt = new Set<number>();
    else if (k === "offenseOffCourt") out.offenseOffCourt = new Set<number>();
    else if (k === "defenseOnCourt") out.defenseOnCourt = new Set<number>();
    else if (k === "defenseOffCourt") out.defenseOffCourt = new Set<number>();
  }
  return out;
}

export function applyActionFilters(
  actions: Action[],
  filters: ActionFilters,
  lineups: LineupsMap = {},
): Action[] {
  const wantAction = filters.actionPresence.has("action");
  const wantNoAction = filters.actionPresence.has("no_action");
  const hasLineupFilter = LINEUP_KEYS.some((k) => filters[k].size > 0);

  const result: Action[] = [];
  for (const action of actions) {
    // Chance-level filters apply to ALL actions (including no_action)
    if (
      filters.transition.size > 0 &&
      (action.transition === null || !filters.transition.has(action.transition))
    )
      continue;
    if (
      filters.chanceType.size > 0 &&
      (action.chanceType === null || !filters.chanceType.has(action.chanceType))
    )
      continue;
    if (
      filters.zone.size > 0 &&
      (action.zone === null || !filters.zone.has(action.zone))
    )
      continue;

    // Lineup filter is chance-scoped — applies to all actions including no_action
    if (hasLineupFilter && !passesLineupFilters(action, filters, lineups))
      continue;

    if (action.actionType === "no_action") {
      if (wantNoAction) result.push(action);
      continue;
    }
    if (wantAction && passesActionFilters(action, filters)) {
      result.push(action);
    }
  }
  return result;
}

/**
 * Apply all filters EXCEPT one category. Used for computing cross-filter counts
 * (shows "how many actions match if I toggle this option?").
 */
/**
 * Apply all filters EXCEPT one category. Used for computing cross-filter counts.
 * Accepts both standard filter keys and type-specific field names (e.g., "bhrDefense").
 */
export function applyActionFiltersExcept(
  actions: Action[],
  filters: ActionFilters,
  skipCategory: keyof ActionFilters | string,
  lineups: LineupsMap = {},
): Action[] {
  // Create a copy of filters with the skipped category set to "all"
  const modifiedFilters = { ...filters };

  switch (skipCategory) {
    case "actionPresence":
      modifiedFilters.actionPresence = new Set<"action" | "no_action">([
        "action",
        "no_action",
      ]);
      break;
    case "transition":
      modifiedFilters.transition = new Set<string>(); // Empty = all
      break;
    case "chanceType":
      modifiedFilters.chanceType = new Set<string>(); // Empty = all
      break;
    case "zone":
      modifiedFilters.zone = new Set<string>(); // Empty = all
      break;
    case "actionType":
      modifiedFilters.actionType = new Set<ActionType>([
        "picks",
        "handoffs",
        "isolations",
        "posts",
        "drives",
        "off_ball_screens",
        "fake_handoffs",
      ]);
      break;
    case "actionSimple":
      modifiedFilters.actionSimple = new Set<string>(); // Empty = all
      break;
    case "actionSize":
      modifiedFilters.actionSize = new Set<ActionSize>([
        "big",
        "small",
        "small_small",
        "small_big",
        "big_big",
        "big_small",
        "none",
      ]);
      break;
    case "direct":
      modifiedFilters.direct = new Set<"direct" | "indirect">([
        "direct",
        "indirect",
      ]);
      break;
    case "primaryOutcome":
      modifiedFilters.primaryOutcome = new Set<PrimaryOutcome>([
        "finish",
        "pass",
      ]);
      break;
    case "primaryPlayer":
      modifiedFilters.primaryPlayer = new Set<number>(); // Empty = all
      break;
    case "secondaryPlayer":
      modifiedFilters.secondaryPlayer = new Set<number>(); // Empty = all
      break;
    case "primaryDefender":
      modifiedFilters.primaryDefender = new Set<number>(); // Empty = all
      break;
    case "secondaryDefender":
      modifiedFilters.secondaryDefender = new Set<number>(); // Empty = all
      break;
    case "offenseOnCourt":
      modifiedFilters.offenseOnCourt = new Set<number>();
      break;
    case "offenseOffCourt":
      modifiedFilters.offenseOffCourt = new Set<number>();
      break;
    case "defenseOnCourt":
      modifiedFilters.defenseOnCourt = new Set<number>();
      break;
    case "defenseOffCourt":
      modifiedFilters.defenseOffCourt = new Set<number>();
      break;
    case "typeSpecific":
      modifiedFilters.typeSpecific = {}; // Clear all type-specific filters
      break;
    default:
      // Type-specific field name — remove just that field from typeSpecific
      if (skipCategory in filters.typeSpecific) {
        const newTypeSpecific = { ...filters.typeSpecific };
        delete newTypeSpecific[skipCategory];
        modifiedFilters.typeSpecific = newTypeSpecific;
      }
      break;
  }

  return applyActionFilters(actions, modifiedFilters, lineups);
}

/**
 * Toggle a filter value within a category.
 * Prevents deselecting the last option in chip-based categories.
 * For player dropdowns (empty = all), toggling removes/adds from the set.
 */
export function toggleActionFilter<K extends keyof ActionFilters>(
  filters: ActionFilters,
  category: K,
  value: ActionFilters[K] extends Set<infer T> ? T : never,
): ActionFilters {
  const existing = filters[category];
  if (!(existing instanceof Set)) return filters; // non-Set categories (lineupMode, typeSpecific) aren't toggled here
  const newSet = new Set(existing as Set<unknown>);

  if (newSet.has(value)) {
    // Multi-select dropdowns (players, lineups) can be empty = all.
    // Chip groups must keep at least one option selected.
    const isMultiSelect =
      category === "primaryPlayer" ||
      category === "secondaryPlayer" ||
      category === "primaryDefender" ||
      category === "secondaryDefender" ||
      category === "offenseOnCourt" ||
      category === "offenseOffCourt" ||
      category === "defenseOnCourt" ||
      category === "defenseOffCourt";
    if (isMultiSelect || newSet.size > 1) {
      newSet.delete(value);
    }
  } else {
    newSet.add(value);
  }

  return { ...filters, [category]: newSet };
}

// ============================================================================
// Possession-Level Deduplication
// ============================================================================

/**
 * Unique chance data for possession-level calculations.
 */
export interface ChanceData {
  aPts: number;
  ePts: number;
  chanceOutcome: string | null;
  /** Counts toward PPP / xPPP (Action.valid; unknown counts) */
  valid: boolean;
}

/**
 * Extract unique chances from filtered actions, deduplicated by chanceId.
 * Multiple actions in the same possession share the same chanceId and aPts/ePts values.
 * We take the first action per chanceId.
 */
export function getUniqueChances(actions: Action[]): Map<string, ChanceData> {
  const chances = new Map<string, ChanceData>();
  for (const action of actions) {
    if (action.chanceId && !chances.has(action.chanceId)) {
      chances.set(action.chanceId, {
        aPts: action.aPts ?? 0,
        ePts: action.ePts ?? 0,
        chanceOutcome: action.chanceOutcome,
        valid: action.valid !== false,
      });
    }
  }
  return chances;
}

// ============================================================================
// Display Formatting
// ============================================================================

/**
 * Format an action type for display.
 * "off_ball_screens" → "Off Ball Screens"
 * "picks" → "Picks"
 */
export function formatActionType(actionType: string): string {
  return actionType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format an action simple value for display.
 * Most are already readable abbreviations (PR, DHO, Iso, etc.)
 */
export function formatActionSimple(actionSimple: string): string {
  return actionSimple;
}

/**
 * Format an action size for display.
 * "small_big" → "Small-Big"
 */
export function formatActionSize(actionSize: string): string {
  return actionSize
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("-");
}

// ============================================================================
// Partition & Group Formatting
// ============================================================================

const PLAYER_FIELDS = new Set([
  "primaryPlayer",
  "secondaryPlayer",
  "primaryDefender",
  "secondaryDefender",
]);

/**
 * Optional context for partition/breakdown operations.
 * Lineup group keys require `lineups` + `filters`; player label resolution
 * requires `players`.
 */
export interface BreakdownCtx {
  lineups?: LineupsMap;
  filters?: ActionFilters;
  players?: string[];
}

/**
 * Partition an already-filtered action array by a filter group key.
 * Returns Map<displayLabel, Action[]> sorted by count descending.
 *
 * For lineup keys, behavior depends on filter state (see partitionByLineup).
 * For player keys, raw indices are resolved to names via ctx.players.
 */
export function partitionActionsByGroup(
  actions: Action[],
  groupKey: string,
  ctx?: BreakdownCtx,
): Map<string, Action[]> {
  if (isLineupKey(groupKey)) {
    return partitionByLineup(actions, groupKey, ctx);
  }

  const players = ctx?.players ?? [];
  const partitions = new Map<string, Action[]>();

  for (const action of actions) {
    let value: string;

    // Chance-level group keys
    if (groupKey === "actionPresence") {
      value = action.actionType === "no_action" ? "no_action" : "action";
    } else if (groupKey === "transition") {
      if (action.transition === null) continue;
      value = action.transition;
    } else if (groupKey === "chanceType") {
      if (action.chanceType === null) continue;
      value = action.chanceType;
    } else if (groupKey === "zone") {
      if (action.zone === null) continue;
      value = action.zone;
    } else if (groupKey === "direct") {
      value = action.direct === true ? "direct" : "indirect";
    } else if (groupKey === "primaryOutcome") {
      if (action.primaryOutcome === null) continue;
      value = action.primaryOutcome;
    } else if (PLAYER_FIELDS.has(groupKey)) {
      const raw = (action as any)[groupKey] as number | null | undefined;
      if (raw === null || raw === undefined) continue;
      value = players[raw] ?? `#${raw}`;
    } else {
      // actionType, actionSimple, actionSize, and type-specific fields
      const raw = (action as any)[groupKey];
      if (raw === undefined || raw === null) continue;
      value = String(raw);
    }

    if (!partitions.has(value)) {
      partitions.set(value, []);
    }
    partitions.get(value)!.push(action);
  }

  // Sort by count descending
  const sorted = new Map<string, Action[]>(
    Array.from(partitions.entries()).sort((a, b) => b[1].length - a[1].length),
  );
  return sorted;
}

/**
 * Partition actions by a lineup key. Three cases:
 * - No selection → per-player presence rows (overlapping — an action appears in
 *   every row whose player was in its chance's lineup). Rows use player names.
 * - N ≥ 1 selected, "any" mode → two rows: "any on" / "none on" (or "off" variants).
 * - N ≥ 1 selected, "all" mode → two rows: "all on" / "not all on" (or "off" variants).
 *
 * For N = 1, labels collapse to "{name} on" / "{name} off".
 *
 * Returns Map<displayLabel, Action[]>. Caller decides whether rows overlap
 * (detectable by comparing sum of sizes vs parent total).
 */
function partitionByLineup(
  actions: Action[],
  key: LineupKey,
  ctx?: BreakdownCtx,
): Map<string, Action[]> {
  const lineups = ctx?.lineups ?? {};
  const filters = ctx?.filters;
  const players = ctx?.players ?? [];
  const side = lineupSide(key);
  const onCourt = lineupOnCourt(key);
  const sel = filters?.[key] ?? new Set<number>();
  const mode = filters?.lineupMode[key] ?? "all";
  const partitions = new Map<string, Action[]>();

  const resolveName = (idx: number): string => players[idx] ?? `#${idx}`;

  if (sel.size === 0) {
    // Per-player presence — rows overlap (an action appears under every player
    // that was on the relevant side for its chance).
    if (onCourt) {
      for (const action of actions) {
        const cid = action.chanceId;
        if (!cid) continue;
        const lu = lineups[cid];
        if (!lu) continue;
        for (const pIdx of lu[side]) {
          const label = resolveName(pIdx);
          if (!partitions.has(label)) partitions.set(label, []);
          partitions.get(label)!.push(action);
        }
      }
    } else {
      // Off-court with no selection: for each player ever on this side in the
      // sample, bucket actions where that player was NOT in the chance's lineup.
      const universe = new Set<number>();
      for (const lu of Object.values(lineups)) {
        for (const p of lu[side]) universe.add(p);
      }
      for (const pIdx of universe) {
        const label = resolveName(pIdx);
        const group: Action[] = [];
        for (const action of actions) {
          const cid = action.chanceId;
          if (!cid) continue;
          const lu = lineups[cid];
          if (!lu) continue;
          if (!lu[side].includes(pIdx)) group.push(action);
        }
        if (group.length > 0) partitions.set(label, group);
      }
    }
  } else {
    // Binary split — match vs not-match.
    const selArr = [...sel];
    const selNames = selArr.map(resolveName).join(", ");
    const { match, notMatch } = binaryLineupLabels(
      mode,
      onCourt,
      selNames,
      selArr.length === 1,
    );
    partitions.set(match, []);
    partitions.set(notMatch, []);

    for (const action of actions) {
      const cid = action.chanceId;
      if (!cid) continue;
      const lu = lineups[cid];
      if (!lu) continue;

      const pool = new Set(lu[side]);
      let matches: boolean;
      if (onCourt) {
        matches = mode === "all" ? allIn(sel, pool) : anyIn(sel, pool);
      } else {
        matches = mode === "all" ? allOut(sel, pool) : anyOut(sel, pool);
      }
      partitions.get(matches ? match : notMatch)!.push(action);
    }
  }

  return new Map<string, Action[]>(
    Array.from(partitions.entries()).sort((a, b) => b[1].length - a[1].length),
  );
}

function binaryLineupLabels(
  mode: "any" | "all",
  onCourt: boolean,
  selNames: string,
  isSingle: boolean,
): { match: string; notMatch: string } {
  if (isSingle) {
    // Single player — "{name} on" / "{name} off"
    const onLabel = `${selNames} on`;
    const offLabel = `${selNames} off`;
    return onCourt
      ? { match: onLabel, notMatch: offLabel }
      : { match: offLabel, notMatch: onLabel };
  }
  const suffix = onCourt ? "on" : "off";
  if (mode === "all") {
    return {
      match: `All of ${selNames} ${suffix}`,
      notMatch: `Not all of ${selNames} ${suffix}`,
    };
  }
  return {
    match: `Any of ${selNames} ${suffix}`,
    notMatch: `None of ${selNames} ${suffix}`,
  };
}

/**
 * Reconstruct the membership predicate for a specific (groupKey, partition
 * value) pair — used to accumulate ancestor-path constraints through nested
 * breakdowns. Covers all breakdown axes (lineup keys, chance-level fields,
 * action fields, player fields, type-specific fields).
 */
function makePartitionPredicate(
  key: string,
  value: string,
  ctx?: BreakdownCtx,
): (a: Action) => boolean {
  if (isLineupKey(key)) {
    if (!ctx?.filters || !ctx?.lineups || !ctx?.players) return () => true;
    return makeLineupPathPredicate(key, value, ctx.filters, ctx.lineups, ctx.players);
  }

  const players = ctx?.players ?? [];
  const resolve = (idx: number | null | undefined): string | null =>
    idx === null || idx === undefined ? null : players[idx] ?? `#${idx}`;

  switch (key) {
    case "actionPresence":
      return (a) => (a.actionType === "no_action" ? "no_action" : "action") === value;
    case "transition":
      return (a) => a.transition === value;
    case "chanceType":
      return (a) => a.chanceType === value;
    case "zone":
      return (a) => a.zone === value;
    case "actionType":
      return (a) => a.actionType === value;
    case "actionSimple":
      return (a) => a.actionSimple === value;
    case "actionSize":
      return (a) => a.actionSize === value;
    case "direct":
      return (a) => (a.direct === true ? "direct" : "indirect") === value;
    case "primaryOutcome":
      return (a) => a.primaryOutcome === value;
    case "primaryPlayer":
      return (a) => resolve(a.primaryPlayer) === value;
    case "secondaryPlayer":
      return (a) => resolve(a.secondaryPlayer) === value;
    case "primaryDefender":
      return (a) => resolve(a.primaryDefender) === value;
    case "secondaryDefender":
      return (a) => resolve(a.secondaryDefender) === value;
    default: {
      // Type-specific field
      return (a) => {
        const raw = (a as any)[key];
        if (raw === undefined || raw === null) return false;
        return String(raw) === value;
      };
    }
  }
}

function makeLineupPathPredicate(
  key: LineupKey,
  partitionValue: string,
  filters: ActionFilters,
  lineups: LineupsMap,
  players: string[],
): (a: Action) => boolean {
  const sel = filters[key];
  const mode = filters.lineupMode[key];
  const side = lineupSide(key);
  const onCourt = lineupOnCourt(key);

  if (sel.size === 0) {
    // Per-player presence — value is a player name; resolve to index.
    const playerIdx = players.indexOf(partitionValue);
    if (playerIdx === -1) return () => false;
    return (a) => {
      const lu = a.chanceId ? lineups[a.chanceId] : undefined;
      if (!lu) return false;
      const has = lu[side].includes(playerIdx);
      return onCourt ? has : !has;
    };
  }

  // Binary partition — determine whether value is the "match" or "not match" label.
  const selArr = Array.from(sel);
  const selNames = selArr.map((i) => players[i] ?? `#${i}`).join(", ");
  const { match } = binaryLineupLabels(mode, onCourt, selNames, selArr.length === 1);
  const isMatch = partitionValue === match;

  return (a) => {
    const lu = a.chanceId ? lineups[a.chanceId] : undefined;
    if (!lu) return false;
    const pool = new Set(lu[side]);
    let matches: boolean;
    if (onCourt) {
      matches = mode === "all" ? allIn(sel, pool) : anyIn(sel, pool);
    } else {
      matches = mode === "all" ? allOut(sel, pool) : anyOut(sel, pool);
    }
    return matches === isMatch;
  };
}

/**
 * Format a partition key for display in the summary table.
 */
export function formatGroupValue(groupKey: string, value: string): string {
  // Shot filter keys have their own formatter
  if (isShotFilterKey(groupKey)) return formatShotGroupValue(groupKey, value);

  // Lineup keys: partition already encodes the display label (player name or
  // composite "All of X, Y on" etc.), so pass-through.
  if (isLineupKey(groupKey)) return value;

  switch (groupKey) {
    case "actionPresence":
      return value === "action" ? "Action" : "No Action";
    case "transition":
    case "chanceType":
    case "zone":
      return value.charAt(0).toUpperCase() + value.slice(1);
    case "actionType":
      return formatActionType(value);
    case "actionSize":
      return formatActionSize(value);
    case "direct":
      return value === "direct" ? "Direct" : "Indirect";
    case "primaryOutcome":
      return value === "finish" ? "Finish" : "Pass";
    case "pickType":
      return pickTypeLabel(value);
    default:
      // Player/defender names — pass through (partition already resolved via players table)
      if (PLAYER_FIELDS.has(groupKey)) return value;
      // Boolean type-specific fields
      if (value === "true") return "Yes";
      if (value === "false") return "No";
      // Other type-specific string fields — title case
      return value
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
}

/**
 * Get the display label for a filter group key.
 * Maps internal field names to the labels shown in the filter sidebar.
 */
const COMMON_GROUP_LABELS: Record<string, string> = {
  actionPresence: "Action",
  transition: "Transition",
  chanceType: "Chance Type",
  zone: "Coverage",
  direct: "Direct",
  actionType: "Action Type",
  actionSimple: "Action",
  actionSize: "Size Matchup",
  primaryOutcome: "Outcome",
  primaryPlayer: "Primary Player",
  secondaryPlayer: "Secondary Player",
  primaryDefender: "Primary Defender",
  secondaryDefender: "Secondary Defender",
  offenseOnCourt: "Offense On Court",
  offenseOffCourt: "Offense Off Court",
  defenseOnCourt: "Defense On Court",
  defenseOffCourt: "Defense Off Court",
};

export function formatGroupLabel(groupKey: string): string {
  if (COMMON_GROUP_LABELS[groupKey]) return COMMON_GROUP_LABELS[groupKey];
  if (SHOT_GROUP_LABELS[groupKey]) return SHOT_GROUP_LABELS[groupKey];

  // Check type-specific fields across all action types
  for (const actionType of ALL_ACTION_TYPES) {
    const fields = TYPE_SPECIFIC_FIELDS[actionType];
    const match = fields.find((f) => f.field === groupKey);
    if (match) return match.label;
  }

  // Fallback: title-case the field name
  return groupKey
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// ============================================================================
// Row Stats Calculation
// ============================================================================

export interface RowStats {
  actionCount: number;
  aPPP: number;
  ePPP: number;
  tovPct: number;
  orbPct: number | null;
  shotCount: number;
  twoPtPct: number | null;
  threePtPct: number | null;
  tsPct: number | null;
  rimRate: number;
  rimTs: number | null;
  midRate: number;
  midTs: number | null;
  threeRate: number;
  threeTs: number | null;
}

/**
 * Calculate all summary metrics for a subset of actions + linked shots.
 * Reusable for Overall, breakdown, and hierarchical sub-breakdown rows.
 */
export function calculateRowStats(
  actions: Action[],
  linkedShots: Shot[],
): RowStats {
  const empty: RowStats = {
    actionCount: 0,
    aPPP: 0,
    ePPP: 0,
    tovPct: 0,
    orbPct: null,
    shotCount: 0,
    twoPtPct: null,
    threePtPct: null,
    tsPct: null,
    rimRate: 0,
    rimTs: null,
    midRate: 0,
    midTs: null,
    threeRate: 0,
    threeTs: null,
  };
  if (actions.length === 0 && linkedShots.length === 0) return empty;

  const actionCount = actions.length;

  // Chance-level stats (action metrics — PPP, xPPP, TOV%). PPP / xPPP average valid chances only, as the report
  // and Explorer do (aPP100 / ePP100): a timeout or non-shooting-foul chance otherwise moved them (2026-10-08: the
  // PR row's 1.00 read 102.6 here).
  const chances = getUniqueChances(actions);
  const chanceValues = [...chances.values()].filter((c) => c.valid);
  const chanceCount = chanceValues.length;
  const totalAPts = chanceValues.reduce((sum, c) => sum + c.aPts, 0);
  const totalEPts = chanceValues.reduce((sum, c) => sum + c.ePts, 0);

  const aPPP = chanceCount > 0 ? totalAPts / chanceCount : 0;
  const ePPP = chanceCount > 0 ? totalEPts / chanceCount : 0;

  // TOV% — possession-deduplicated: possessions with TO / total possessions
  // A possession has a TO if any of its chances has chanceOutcome === "TO"
  const possessionHasTO = new Map<string, boolean>();
  for (const action of actions) {
    if (!action.possessionId) continue;
    if (!possessionHasTO.has(action.possessionId)) {
      possessionHasTO.set(action.possessionId, false);
    }
    if (action.chanceOutcome === "TO") {
      possessionHasTO.set(action.possessionId, true);
    }
  }
  const possessionCount = possessionHasTO.size;
  const turnovers =
    possessionCount > 0
      ? [...possessionHasTO.values()].filter(Boolean).length
      : 0;
  const tovPct = possessionCount > 0 ? turnovers / possessionCount : 0;

  // ORB% — chance-deduplicated: ORB / (ORB + DRB)
  // Build chanceId → rebounded map, preferring non-null values
  // (the first action in a chance may have null while a later one has the rebound)
  const chanceRebounded = new Map<string, string | null>();
  for (const action of actions) {
    if (!action.chanceId) continue;
    const existing = chanceRebounded.get(action.chanceId);
    if (existing === undefined) {
      chanceRebounded.set(action.chanceId, action.rebounded);
    } else if (existing === null && action.rebounded !== null) {
      chanceRebounded.set(action.chanceId, action.rebounded);
    }
  }
  let orbCount = 0;
  let drbCount = 0;
  for (const reb of chanceRebounded.values()) {
    if (reb === "offensive") orbCount++;
    else if (reb === "defensive") drbCount++;
  }
  const totalRebounds = orbCount + drbCount;
  const orbPct = totalRebounds > 0 ? orbCount / totalRebounds : null;

  // Shooting stats from pre-linked shots (caller scopes the shot pool)
  const shotCount = linkedShots.length;
  const totalPts = linkedShots.reduce((sum, s) => sum + s.ptsScored, 0);
  const tsPct = shotCount > 0 ? totalPts / shotCount / 2 : null;

  // 2Pt% and 3Pt% (exclude missed non-fouled shots from denominator)
  const twoPtShots = linkedShots.filter((s) => !s.three);
  const threePtShots = linkedShots.filter((s) => s.three);
  const twoPtPct = computeFgPct(twoPtShots);
  const threePtPct = computeFgPct(threePtShots);

  // Zone partitioning
  const rimShots = linkedShots.filter(isRimShot);
  const midShots = linkedShots.filter(isMidShot);

  const zoneStats = (shots: Shot[]) => {
    const count = shots.length;
    const pts = shots.reduce((sum, s) => sum + s.ptsScored, 0);
    return {
      rate: shotCount > 0 ? count / shotCount : 0,
      ts: count > 0 ? pts / count / 2 : null,
    };
  };

  const rim = zoneStats(rimShots);
  const mid = zoneStats(midShots);
  const three = zoneStats(threePtShots);

  return {
    actionCount,
    aPPP,
    ePPP,
    tovPct,
    orbPct,
    shotCount,
    twoPtPct,
    threePtPct,
    tsPct,
    rimRate: rim.rate,
    rimTs: rim.ts,
    midRate: mid.rate,
    midTs: mid.ts,
    threeRate: three.rate,
    threeTs: three.ts,
  };
}

/**
 * Calculate stats for a shot-level breakdown row.
 * Returns RowStats with action metrics zeroed — the renderer shows "—" for these.
 */
export function calculateShotRowStats(shots: Shot[]): RowStats {
  const count = shots.length;
  const empty: RowStats = {
    actionCount: 0,
    aPPP: 0,
    ePPP: 0,
    tovPct: 0,
    orbPct: null,
    shotCount: 0,
    twoPtPct: null,
    threePtPct: null,
    tsPct: null,
    rimRate: 0,
    rimTs: null,
    midRate: 0,
    midTs: null,
    threeRate: 0,
    threeTs: null,
  };
  if (count === 0) return empty;

  const totalPts = shots.reduce((sum, s) => sum + s.ptsScored, 0);
  const tsPct = totalPts / count / 2;

  const twoPtShots = shots.filter((s) => !s.three);
  const threePtShots = shots.filter((s) => s.three);
  const twoPtPct = computeFgPct(twoPtShots);
  const threePtPct = computeFgPct(threePtShots);

  const rimShots = shots.filter(isRimShot);
  const midShots = shots.filter(isMidShot);

  const zoneStats = (zoneShotList: Shot[]) => {
    const n = zoneShotList.length;
    const pts = zoneShotList.reduce((sum, s) => sum + s.ptsScored, 0);
    return {
      rate: count > 0 ? n / count : 0,
      ts: n > 0 ? pts / n / 2 : null,
    };
  };

  const rim = zoneStats(rimShots);
  const mid = zoneStats(midShots);
  const three = zoneStats(threePtShots);

  return {
    actionCount: 0,
    aPPP: 0,
    ePPP: 0,
    tovPct: 0,
    orbPct: null,
    shotCount: count,
    twoPtPct,
    threePtPct,
    tsPct,
    rimRate: rim.rate,
    rimTs: rim.ts,
    midRate: mid.rate,
    midTs: mid.ts,
    threeRate: three.rate,
    threeTs: three.ts,
  };
}

// ============================================================================
// Hierarchical Breakdown
// ============================================================================

/**
 * A single row in the hierarchical breakdown table.
 * Produced by recursively partitioning actions through the group hierarchy.
 */
export interface HierarchicalRow {
  /** Nesting depth: 1 = first-level group, 2 = second-level, etc. */
  depth: number;
  /** Display label for this row */
  label: string;
  /** The group key that produced this row */
  groupKey: string;
  /** Computed stats for this row's action subset */
  stats: RowStats;
  /** The action subset this row was computed from (for action chart highlighting) */
  actions: Action[];
  /** Linked shots for this row (for shot chart highlighting) */
  shots: Shot[];
  /** Action count of the parent (for parent-relative percentages) */
  parentActionCount: number;
  /** Whether this row has children below it (for styling as a group header) */
  isGroupHeader: boolean;
  /** Unique key for React rendering (path of values joined) */
  rowKey: string;
  /** True if this row was produced by a shot filter key (shows only shooting metrics) */
  isShotBreakdown: boolean;
  /**
   * True when rows in this partition overlap (e.g. per-player presence breakdown
   * where the same action appears in multiple rows). Sum of row counts > parent
   * total. Renderer should communicate this to avoid misleading sums/percentages.
   */
  isOverlapping?: boolean;
}

/**
 * Build a flat array of hierarchical breakdown rows.
 *
 * Supports both action and shot filter keys in the hierarchy:
 * - Action filter keys: partition actions, compute full stats (aPPP, ePPP, TOV%, TS%)
 * - Shot filter keys: switch to shot mode, partition linked shots, compute shot-only stats
 *
 * Once a shot filter key is encountered, all subsequent levels stay in shot mode.
 * Action filter keys cannot appear after shot filter keys (enforced by handleExpandGroup).
 *
 * Output is a flat array ordered depth-first for table rendering.
 */
export function buildHierarchicalRows(
  actions: Action[],
  linkedShots: Shot[],
  groupKeys: string[],
  ctx?: BreakdownCtx & {
    rootActions?: Action[];
    /**
     * Shot pool that mirrors the overall filter state EXCEPT lineup filters —
     * needed so lineup breakdowns (which ignore their own-axis filter) have
     * shots for the "off" partition that would otherwise be hidden by an active
     * lineup filter on `linkedShots`.
     */
    rootLinkedShots?: Shot[];
  },
): HierarchicalRow[] {
  if (groupKeys.length === 0) return [];

  const rows: HierarchicalRow[] = [];

  // When any lineup key appears in the breakdown, precompute a "broad" action
  // pool with ONLY the in-breakdown lineup filters stripped — out-of-breakdown
  // lineup filters still apply. Lineup breakdowns at any depth use this pool
  // plus an accumulated ancestor-path predicate to narrow.
  const inBreakdownLineupKeys = groupKeys.filter(isLineupKey) as LineupKey[];
  const hasLineupBreakdown = inBreakdownLineupKeys.length > 0;
  const broadActions: Action[] =
    hasLineupBreakdown && ctx?.rootActions && ctx.filters && ctx.lineups
      ? applyActionFilters(
          ctx.rootActions,
          stripLineupFilters(ctx.filters, inBreakdownLineupKeys),
          ctx.lineups,
        )
      : actions;
  const broadShotPool: Shot[] = ctx?.rootLinkedShots ?? linkedShots;

  /** Recursion in action mode — partitions actions. */
  function recurseAction(
    subset: Action[],
    remainingKeys: string[],
    depth: number,
    parentCount: number,
    pathPrefix: string,
    /**
     * Predicate encoding ancestor-breakdown constraints from prior levels in
     * the hierarchy (e.g. transition="halfcourt" and "Thybulle on defense").
     * Used when a descendant lineup key broadens against the pre-lineup-filter
     * pool — the ancestor predicate narrows it to this partition's path.
     */
    ancestorPred?: (a: Action) => boolean,
  ) {
    if (remainingKeys.length === 0) return;

    const [currentKey, ...nextKeys] = remainingKeys;

    // If we hit a shot filter key, transition to shot mode
    if (isShotFilterKey(currentKey)) {
      const chanceIds = new Set(
        subset.map((a) => a.chanceId).filter((id): id is string => id !== null),
      );
      // Scope shots to this action subset's chances.
      const subsetShots = linkedShots.filter(
        (s) => s.chanceId !== null && chanceIds.has(s.chanceId),
      );
      recurseShot(
        subsetShots,
        remainingKeys,
        depth,
        subsetShots.length,
        pathPrefix,
        subset,
      );
      return;
    }

    // Lineup keys at any depth: operate on the broadened pool (in-breakdown
    // lineup filters stripped) narrowed by the accumulated ancestor predicate.
    // This makes both own-axis filter ignoring and nested lineup breakdowns
    // work: each level's own filter is stripped once at the root, and
    // ancestor levels contribute their partition-membership predicate.
    // Non-lineup levels use the inherited subset — which, when any lineup key
    // is in the breakdown, is already the broadened pool from the root call.
    let partitionSubset = subset;
    let partitionParentCount = parentCount;
    let partitionShotPool = hasLineupBreakdown ? broadShotPool : linkedShots;
    const isLineupBreakdown =
      isLineupKey(currentKey) &&
      !!ctx?.rootActions &&
      !!ctx?.filters &&
      !!ctx?.lineups &&
      !!ctx?.players;
    if (isLineupBreakdown) {
      partitionSubset = ancestorPred
        ? broadActions.filter(ancestorPred)
        : broadActions;
      partitionParentCount = partitionSubset.length;
      partitionShotPool = broadShotPool;
    }

    const partitions = partitionActionsByGroup(partitionSubset, currentKey, ctx);

    // Detect overlap: sum of row sizes > input size means rows overlap.
    let sumSizes = 0;
    for (const [, v] of partitions) sumSizes += v.length;
    const overlapping =
      isLineupKey(currentKey) &&
      ctx?.filters !== undefined &&
      ctx.filters[currentKey as LineupKey].size === 0 &&
      sumSizes > partitionSubset.length;

    // Cap nesting only when BOTH current and child would be overlapping
    // (presence-style). Presence→binary is tractable (N × 2 rows); presence→
    // presence would explode (N × N) and is the case worth blocking.
    const childKey = nextKeys[0];
    const childWouldBeOverlapping =
      childKey !== undefined &&
      isLineupKey(childKey) &&
      !!ctx?.filters &&
      ctx.filters[childKey as LineupKey].size === 0;
    const hasChildren =
      nextKeys.length > 0 && !(overlapping && childWouldBeOverlapping);

    for (const entry of Array.from(partitions.entries())) {
      const value: string = entry[0];
      const groupActions: Action[] = entry[1];
      const rowKey = pathPrefix
        ? `${pathPrefix}|${currentKey}:${value}`
        : `${currentKey}:${value}`;

      // Compute linked shots for this action subset (excludes orphans — they have no action group)
      const groupChanceIds = new Set(
        groupActions
          .map((a) => a.chanceId)
          .filter((id): id is string => id !== null),
      );
      const groupShots = partitionShotPool.filter(
        (s) => s.chanceId !== null && groupChanceIds.has(s.chanceId),
      );

      rows.push({
        depth,
        label: formatGroupValue(currentKey, value),
        groupKey: currentKey,
        stats: calculateRowStats(groupActions, groupShots),
        actions: groupActions,
        shots: groupShots,
        parentActionCount: partitionParentCount,
        isGroupHeader: hasChildren,
        isShotBreakdown: false,
        rowKey,
        isOverlapping: overlapping,
      });

      if (hasChildren) {
        // Accumulate this partition's predicate so descendant lineup levels
        // can narrow correctly against the broad pool.
        const thisLevel = makePartitionPredicate(currentKey, value, ctx);
        const childAncestorPred: (a: Action) => boolean = ancestorPred
          ? (a) => ancestorPred(a) && thisLevel(a)
          : thisLevel;
        recurseAction(
          groupActions,
          nextKeys,
          depth + 1,
          groupActions.length,
          rowKey,
          childAncestorPred,
        );
      }
    }
  }

  /** Recursion in shot mode — partitions shots. All subsequent levels stay in shot mode. */
  function recurseShot(
    shotSubset: Shot[],
    remainingKeys: string[],
    depth: number,
    parentCount: number,
    pathPrefix: string,
    parentActions: Action[], // Keep for chart highlighting
  ) {
    if (remainingKeys.length === 0) return;

    const [currentKey, ...nextKeys] = remainingKeys;
    const partitions = partitionShotsByGroup(shotSubset, currentKey);
    const hasChildren = nextKeys.length > 0;

    for (const [value, groupShots] of partitions) {
      const rowKey = pathPrefix
        ? `${pathPrefix}|${currentKey}:${value}`
        : `${currentKey}:${value}`;

      rows.push({
        depth,
        label: formatGroupValue(currentKey, value),
        groupKey: currentKey,
        stats: calculateShotRowStats(groupShots),
        actions: parentActions,
        shots: groupShots,
        parentActionCount: parentCount,
        isGroupHeader: hasChildren,
        isShotBreakdown: true,
        rowKey,
      });

      if (hasChildren) {
        recurseShot(
          groupShots,
          nextKeys,
          depth + 1,
          groupShots.length,
          rowKey,
          parentActions,
        );
      }
    }
  }

  // When any lineup key is in the breakdown, start the recursion from the
  // broadened pool so non-lineup levels (e.g. Direct/Indirect above a lineup
  // breakdown) partition the same set Overall reflects.
  const rootSubset = hasLineupBreakdown ? broadActions : actions;
  recurseAction(rootSubset, groupKeys, 1, rootSubset.length, "");
  return rows;
}
